/**
 * SEO metadata config, single source of truth for per-page <title>,
 * <meta description>, and canonical URLs.
 *
 * Add a new public route here when you add it in App.js, and also add
 * the path to scripts/routes.js so the prerender + sitemap pick it up.
 *
 * Domain: change SITE.domain in one place to swap the production URL.
 */

export const SITE = {
  name: 'Notapaths',
  domain: 'https://notapaths.com',
  defaultDescription:
    'Notapaths is a global mobility consultancy for individuals. Structured support for studying abroad, working abroad, and permanent residency in Canada, Australia, New Zealand, and Portugal.',
};

/**
 * Each entry:
 *   title: string                  , full <title>; brand suffix included
 *   description: string            , meta description; ≤160 chars
 *   canonical?: string             , override canonical URL (path)
 *                                      Use when this route is a duplicate
 *                                      of another route's content.
 */
export const PAGES = {
  '/': {
    title: 'Notapaths | Study Abroad, Work Abroad & Permanent Residency Support',
    description:
      'Notapaths helps individuals navigate study abroad, work abroad, and permanent residency pathways with clear guidance and end-to-end application support.',
  },
  '/study-abroad': {
    title: 'Study Abroad Support | University Admissions | Notapaths',
    description:
      'Structured study abroad support: university shortlisting, personal statements, document preparation, and application submission for international students.',
  },
  '/work-abroad': {
    title: 'Work Abroad Support | Verified Overseas Jobs | Notapaths',
    description:
      'Work abroad support with verified overseas employers, guaranteed interview opportunities for eligible applicants, and interview readiness coaching.',
  },
  '/pricing': {
    title: 'Pricing | Transparent Fees for Study & Work Abroad | Notapaths',
    description:
      'Transparent pricing for Notapaths study and work abroad services. See what is free, what we charge for, and how we keep fees honest.',
  },
  '/about': {
    title: 'About Notapaths | Global Mobility Consultancy',
    description:
      'Learn about Notapaths: a global mobility consultancy supporting individuals with study, work, and permanent residency pathways worldwide.',
  },
  '/contact': {
    title: 'Contact Notapaths | Book a Consultation',
    description:
      'Get in touch with Notapaths. Book a consultation about studying abroad, working abroad, or permanent residency and our team will respond promptly.',
  },

  // ─── Permanent Residency ───────────────────────────────────────────────
  '/permanent-residency/canada': {
    title: 'Canada Permanent Residency | PR Pathways & Support | Notapaths',
    description:
      'Canada permanent residency support: Express Entry, PNP, and family pathways. Eligibility review, document preparation, and application guidance.',
  },
  '/permanent-residency/australia': {
    title: 'Australia Permanent Residency | PR Pathways & Support | Notapaths',
    description:
      'Australia permanent residency support: skilled migration, employer-sponsored, and family pathways. Eligibility review and application preparation.',
  },
  '/permanent-residency/australia-care-workforce': {
    title: 'Australia Care Workforce Program | Aged Care & Disability Support | Notapaths',
    description:
      'Australia Care Workforce program for aged care and disability support roles. Screening, training, and sponsored employment pathways for eligible candidates.',
  },
  '/permanent-residency/new-zealand': {
    title: 'New Zealand Permanent Residency | PR Pathways & Support | Notapaths',
    description:
      'New Zealand permanent residency support: Skilled Migrant Category, work-to-residence, and family pathways with document preparation and guidance.',
  },
  '/permanent-residency/portugal': {
    title: 'Portugal Permanent Residency | PR Pathways & Support | Notapaths',
    description:
      'Portugal permanent residency support: D7, D8 digital nomad, work and family pathways. Eligibility review, document preparation, and application guidance.',
  },

  // ─── Blog ──────────────────────────────────────────────────────────────
  '/blog': {
    title: 'Notapaths Blog | Study Abroad & Work Abroad Insights',
    description:
      'Practical articles on studying abroad, working abroad, and permanent residency. Guides, checklists, and decision frameworks from the Notapaths team.',
  },
  '/blog/practical-guide-studying-abroad': {
    title: 'A Practical Guide to Studying Abroad: From Research to Admission | Notapaths',
    description:
      'Step-by-step guide to studying abroad: choosing the right country and university, preparing documents, and submitting strong applications.',
  },
  '/blog/working-abroad-preparation': {
    title: 'Working Abroad: How to Prepare for International Job Opportunities | Notapaths',
    description:
      'How to prepare for overseas employment: employer expectations, interview readiness, and the documents you need to pursue work abroad with confidence.',
  },
  '/blog/informed-decisions-study-work-abroad': {
    title: 'Making Informed Decisions About Study and Work Abroad | Notapaths',
    description:
      'Key factors to weigh before studying or working abroad: eligibility, timelines, costs, and how professional guidance helps you avoid common mistakes.',
  },

  // ─── Legal ─────────────────────────────────────────────────────────────
  '/terms-of-service': {
    title: 'Terms of Service | Notapaths',
    description:
      'Terms of service governing use of the Notapaths website and our study abroad, work abroad, and permanent residency consulting engagements.',
  },
  '/privacy-policy': {
    title: 'Privacy Policy | Notapaths',
    description:
      'How Notapaths collects, uses, stores, and protects personal data when you use our site or engage our global mobility consulting services.',
  },
  '/cookie-policy': {
    title: 'Cookie Policy | Notapaths',
    description:
      'How Notapaths uses cookies and similar technologies on our websites, client portals, and emails.',
  },
};

/**
 * Resolve metadata for a given pathname. Falls back to homepage metadata
 * if the route is unknown (e.g., 404 page during prerender).
 */
export function getPageMeta(pathname) {
  return PAGES[pathname] || PAGES['/'];
}
